/**
 * @fileoverview Dynamic SEO Metadata Component
 * 
 * This component dynamically updates metadata tags in the document head
 * based on the current language. It works with the language context to
 * ensure proper localization of titles, descriptions, and OpenGraph tags.
 */

import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/lib/language-context';
import { LocaleType } from '@/types/chat';

// Localized metadata for each supported language
const localizedMetadata: Record<LocaleType, {
  title: string;
  description: string;
  keywords: string;
}> = {
  // Arabic translations
  ar: {
    title: "Leen - خبيرة نظام العمل السعودي | MJ",
    description: "مساعد ذكاء اصطناعي للامتثال لنظام العمل السعودي. احصل على إرشادات فورية حول حقوق العمال، والتشريعات، وسياسات مكان العمل، ومسؤوليات أصحاب العمل.",
    keywords: "وزارة الموارد البشرية والتنمية الاجتماعية، نظام العمل، المملكة العربية السعودية، الامتثال، حقوق العمال، مسؤوليات صاحب العمل، سياسات مكان العمل، نظام العمل السعودي"
  },

  // English translations
  en: {
    title: "Leen - Saudi Labor Law Expert | MJ",
    description: "AI-powered assistant for Saudi HRSD Labor Law compliance. Get instant guidance on labor regulations, employee rights, workplace policies, and employer responsibilities.",
    keywords: "HRSD, labor law, Saudi Arabia, compliance, employee rights, workplace policies, employer responsibilities, Saudi Labor Law"
  },

  // Chinese translations
  zh: {
    title: "Leen - 沙特劳动法专家 | MJ",
    description: "为沙特阿拉伯劳动法合规而设计的人工智能助手。获取关于劳动法规定、员工权利、工作场所政策和雇主责任的即时指导。",
    keywords: "HRSD, 劳动法, 沙特阿拉伯, 合规, 员工权利, 工作场所政策, 雇主责任, 沙特劳动法"
  },

  // Japanese translations
  ja: {
    title: "Leen - サウジ労働法専門家 | MJ",
    description: "サウジアラビアのHRSD労働法コンプライアンスのためのAI支援アシスタント。労働規制、従業員の権利、職場方針、雇用主の責任に関する即時ガイダンスを取得します。",
    keywords: "HRSD、労働法、サウジアラビア、コンプライアンス、従業員の権利、職場方針、雇用主の責任、サウジ労働法"
  },

  // Russian translations
  ru: {
    title: "Leen - Эксперт по трудовому праву Саудовской Аравии | MJ",
    description: "ИИ-помощник для соблюдения трудового законодательства Саудовской Аравии. Получите мгновенные рекомендации по трудовым нормам, правам сотрудников, политикам на рабочем месте и обязанностям работодателя.",
    keywords: "HRSD, трудовое право, Саудовская Аравия, соответствие, права сотрудников, политики на рабочем месте, обязанности работодателя, трудовое законодательство Саудовской Аравии"
  },

  // German translations
  de: {
    title: "Leen - Saudi-Arabische Arbeitsrechtsexpertin | MJ",
    description: "KI-gestützter Assistent für die Einhaltung des saudi-arabischen Arbeitsrechts. Erhalten Sie sofortige Anleitungen zu Arbeitsvorschriften, Arbeitnehmerrechten, Arbeitsplatzrichtlinien und Arbeitgeberpflichten.",
    keywords: "HRSD, Arbeitsrecht, Saudi-Arabien, Compliance, Arbeitnehmerrechte, Arbeitsplatzrichtlinien, Arbeitgeberpflichten, Saudi-Arabisches Arbeitsrecht"
  },

  // French translations
  fr: {
    title: "Leen - Expert en droit du travail saoudien | MJ",
    description: "Assistant alimenté par l'IA pour la conformité au droit du travail saoudien. Obtenez des conseils instantanés sur les réglementations du travail, les droits des employés, les politiques du lieu de travail et les responsabilités des employeurs.",
    keywords: "HRSD, droit du travail, Arabie Saoudite, conformité, droits des employés, politiques du lieu de travail, responsabilités des employeurs, droit du travail saoudien"
  },

  // Spanish translations
  es: {
    title: "Leen - Experta en Derecho Laboral Saudí | MJ",
    description: "Asistente con IA para el cumplimiento de la ley laboral de Arabia Saudí. Obtenga orientación instantánea sobre regulaciones laborales, derechos de los empleados, políticas del lugar de trabajo y responsabilidades del empleador.",
    keywords: "HRSD, derecho laboral, Arabia Saudí, cumplimiento, derechos de los empleados, políticas del lugar de trabajo, responsabilidades del empleador, ley laboral saudí"
  },

  // Hindi translations
  hi: {
    title: "Leen - सऊदी श्रम कानून विशेषज्ञ | MJ",
    description: "सऊदी HRSD श्रम कानून अनुपालन के लिए AI-संचालित सहायक। श्रम नियमों, कर्मचारी अधिकारों, कार्यस्थल नीतियों, और नियोक्ता जिम्मेदारियों पर तत्काल मार्गदर्शन प्राप्त करें।",
    keywords: "HRSD, श्रम कानून, सऊदी अरब, अनुपालन, कर्मचारी अधिकार, कार्यस्थल नीतियां, नियोक्ता जिम्मेदारियां, सऊदी श्रम कानून"
  },

  // Turkish translations
  tr: {
    title: "Leen - Suudi İş Hukuku Uzmanı | MJ",
    description: "Suudi HRSD İş Kanunu uyumluluğu için yapay zeka destekli asistan. İş düzenlemeleri, çalışan hakları, işyeri politikaları ve işveren sorumlulukları hakkında anında rehberlik alın.",
    keywords: "HRSD, iş hukuku, Suudi Arabistan, uyumluluk, çalışan hakları, işyeri politikaları, işveren sorumlulukları, Suudi İş Kanunu"
  },

  // Swedish translations
  sv: {
    title: "Leen - Saudisk Arbetsrättsexpert | MJ",
    description: "AI-driven assistent för efterlevnad av saudisk arbetslagstiftning. Få omedelbar vägledning om arbetsregler, anställdas rättigheter, arbetsplatspolicies och arbetsgivarens ansvar.",
    keywords: "HRSD, arbetsrätt, Saudiarabien, efterlevnad, anställdas rättigheter, arbetsplatspolicies, arbetsgivarens ansvar, saudisk arbetsrätt"
  }
};

/**
 * Updates SEO metadata in the document head based on the current language
 * @returns null - This component doesn't render any visible elements
 */
export function SEOMetadata(): null {
  const { locale, isRTL } = useLanguage();
  
  // Track URL path and search params for language changes
  const [pathAndSearch, setPathAndSearch] = React.useState(() => 
    typeof window !== 'undefined' ? window.location.pathname + window.location.search : '');
  
  // Set up listener for URL path and query changes
  React.useEffect(() => {
    const handleUrlChange = () => {
      // We need to track both pathname and search for path-based localization
      const currentPathWithQuery = window.location.pathname + window.location.search;
      if (pathAndSearch !== currentPathWithQuery) {
        setPathAndSearch(currentPathWithQuery);
      }
    };
    
    // Listen for navigation events
    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);
    
    // Create a MutationObserver to watch for changes to content that might indicate navigation
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => {
        handleUrlChange();
      });
      
      // Observe changes to the document head, which often changes during navigation
      observer.observe(document.head || document.documentElement, { childList: true, subtree: true });
    }
    
    // Set up periodic checks for SPA navigation where events might not fire
    const intervalId = setInterval(handleUrlChange, 1000);
    
    // Force an update on mount to ensure proper initialization
    handleUrlChange();
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
      clearInterval(intervalId);
    };
  }, [pathAndSearch]);
  
  // Initialize with URL parameters
  useEffect(() => {
    // Check URL parameters on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const initialLangParam = urlParams.get('lang');
    
    // Validate language param silently
    // Also check URL path for language code
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const pathLang = pathParts[0] as LocaleType | undefined;
    
    // Trigger a metadata update
    setPathAndSearch(window.location.pathname + window.location.search);
  }, []);

  // Update metadata when locale or URL parameters change
  useEffect(() => {
    try {
      // Check for language in URL path first (new preferred method)
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const pathLang = pathParts[0] as LocaleType | undefined;
      
      // Then check URL parameters as fallback (old method)
      const urlParams = new URLSearchParams(window.location.search);
      const queryLang = urlParams.get('lang') as LocaleType | null;
      
      // Prioritize path language over query parameter
      let detectedLang: LocaleType | null = null;
      
      if (pathLang && Object.keys(localizedMetadata).includes(pathLang)) {
        detectedLang = pathLang;
      } else if (queryLang && Object.keys(localizedMetadata).includes(queryLang)) {
        detectedLang = queryLang;
      }
      
      // Use detected language if available and valid, otherwise use context locale
      const effectiveLocale = detectedLang || locale;
      
      const metadata = localizedMetadata[effectiveLocale] || localizedMetadata.en;
      
      // Update document title
      document.title = metadata.title;
      
      // Update meta tags
      updateMetaTag('title', metadata.title);
      updateMetaTag('description', metadata.description);
      updateMetaTag('keywords', metadata.keywords);
      
      // Update Open Graph tags
      updateMetaTag('og:title', metadata.title, 'property');
      updateMetaTag('og:description', metadata.description, 'property');
      updateMetaTag('og:locale', effectiveLocale === 'en' ? 'en_US' : `${effectiveLocale}_${effectiveLocale.toUpperCase()}`, 'property');
      
      // Update Twitter Card tags
      updateMetaTag('twitter:title', metadata.title);
      updateMetaTag('twitter:description', metadata.description);
      
      // No logging needed for metadata updates
    } catch (error) {
      // Silent catch - metadata update is non-critical
    }
  }, [locale, isRTL, pathAndSearch]);
  
  // Helper function to find or create meta tags
  function updateMetaTag(name: string, content: string, attributeName: 'name' | 'property' = 'name') {
    // Look for existing tag
    let metaTag = document.querySelector(`meta[${attributeName}="${name}"]`);
    
    // Create tag if it doesn't exist
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(attributeName, name);
      document.head.appendChild(metaTag);
    }
    
    // Update content
    metaTag.setAttribute('content', content);
  }
  
  // This component doesn't render anything
  return null;
}

export default SEOMetadata;